import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import UserForm from './components/UserForm';
import UserGrid from './components/UserGrid';
import UserFilters from './components/UserFilters';

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    role: 'all',
    company: 'all',
    status: 'all',
    search: ''
  });

  useEffect(() => {
    loadUsers();
    loadCompanies();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase?.from('user_profiles')?.select(`
          *,
          company:companies!fk_user_profiles_company(id, name)
        `)?.order('created_at', { ascending: false });

      if (error) throw error;
      
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error?.message);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const { data, error } = await supabase?.from('companies')?.select('id, name')?.order('name', { ascending: true });

      if (error) throw error;
      
      setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error?.message);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      setError(null);
      
      // Create auth user
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: userData?.email,
        password: userData?.password,
        options: {
          data: {
            full_name: userData?.name,
            phone: userData?.phone,
            role: userData?.role
          }
        }
      });

      if (authError) throw authError;
      
      // Update user profile with company and plant
      if (authData?.user?.id) {
        const { error: updateError } = await supabase?.from('user_profiles')?.update({
            company_id: userData?.companyId || null,
            plant_id: userData?.plantId || null
          })?.eq('id', authData?.user?.id);

        if (updateError) throw updateError;
      }
      
      await loadUsers();
      setIsFormOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error creating user:', error?.message);
      setError('Failed to create user: ' + error?.message);
    }
  };

  const handleUpdateUser = async (userData) => {
    try {
      setError(null);
      
      const { data, error } = await supabase?.from('user_profiles')?.update({
          full_name: userData?.name,
          phone: userData?.phone,
          role: userData?.role,
          status: userData?.status,
          company_id: userData?.companyId || null,
          plant_id: userData?.plantId || null
        })?.eq('id', editingUser?.id)?.select()?.single();

      if (error) throw error;
      
      await loadUsers();
      setIsFormOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user:', error?.message);
      setError('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        setError(null);
        
        const { error } = await supabase?.from('user_profiles')?.delete()?.eq('id', userId);

        if (error) throw error;
        
        await loadUsers();
      } catch (error) {
        console.error('Error deleting user:', error?.message);
        setError('Failed to delete user');
      }
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsFormOpen(true);
  };

  const handleToggleStatus = async (userId) => {
    try {
      setError(null);
      
      const userToUpdate = users?.find(u => u?.id === userId);
      if (!userToUpdate) return;
      
      const newStatus = userToUpdate?.status === 'active' ? 'inactive' : 'active';
      
      const { error } = await supabase?.from('user_profiles')?.update({ status: newStatus })?.eq('id', userId);

      if (error) throw error;
      
      await loadUsers();
    } catch (error) {
      console.error('Error toggling user status:', error?.message);
      setError('Failed to update user status');
    }
  };

  const filteredUsers = users?.filter(user => {
    const matchesRole = filters?.role === 'all' || user?.role === filters?.role;
    const matchesCompany = filters?.company === 'all' || user?.company_id === filters?.company;
    const matchesStatus = filters?.status === 'all' || user?.status === filters?.status;
    const matchesSearch = 
      user?.full_name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(filters?.search?.toLowerCase());
    return matchesRole && matchesCompany && matchesStatus && matchesSearch;
  });

  const stats = {
    total: users?.length,
    admins: users?.filter(u => u?.role === 'super_admin' || u?.role === 'company_admin')?.length,
    regularUsers: users?.filter(u => u?.role === 'technician' || u?.role === 'viewer')?.length,
    active: users?.filter(u => u?.status === 'active')?.length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading users...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-6 py-8">
        <Breadcrumb
          items={[
            { label: 'Home', path: '/' },
            { label: 'User Management', path: '/user-management' }
          ]}
        />

        <div className="flex items-center justify-between mb-8 mt-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">User Management</h1>
            <p className="text-muted-foreground mt-2">Manage user accounts, roles, and permissions</p>
          </div>
          <Button
            variant="default"
            iconName="UserPlus"
            onClick={() => {
              setEditingUser(null);
              setIsFormOpen(true);
            }}
          >
            Add User
          </Button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Users" size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold text-foreground">{stats?.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Icon name="Shield" size={20} className="text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Admins</p>
                <p className="text-2xl font-bold text-foreground">{stats?.admins}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Icon name="User" size={20} className="text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Regular Users</p>
                <p className="text-2xl font-bold text-foreground">{stats?.regularUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Icon name="CheckCircle" size={20} className="text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-foreground">{stats?.active}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-6">
          <UserFilters
            filters={filters}
            onFilterChange={setFilters}
            companies={companies}
          />

          <UserGrid
            users={filteredUsers}
            companies={companies}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
            onToggleStatus={handleToggleStatus}
          />
        </div>
      </div>
      {isFormOpen && (
        <UserForm
          user={editingUser}
          companies={companies}
          onSubmit={editingUser ? handleUpdateUser : handleCreateUser}
          onClose={() => {
            setIsFormOpen(false);
            setEditingUser(null);
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;